import React, { useMemo } from "react";
import { motion } from "framer-motion";

/*
  Premium confetti burst — mix of rectangular confetti pieces and small
  flag-shaped pieces (UK or UAE). Used as a short celebration moment
  before the redirect loading overlay.
*/

const CONFETTI_COLORS = [
  "#FFAEC9", // pastel pink
  "#C6AFE8", // lavender
  "#AAD0F5", // baby blue
  "#FFE3A3", // champagne
  "#111111", // black
  "#FFFFFF", // white
  "#C9A961", // gold (grad sash)
];

function UKFlag({ size = 18 }) {
  return (
    <svg viewBox="0 0 60 40" width={size} height={size * 0.67} aria-hidden>
      <rect width="60" height="40" fill="#012169" />
      {/* White diagonals */}
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
      {/* Red diagonals */}
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3" />
      {/* White cross */}
      <path d="M30,0 V40 M0,20 H60" stroke="#FFFFFF" strokeWidth="10" />
      {/* Red cross */}
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function UAEFlag({ size = 18 }) {
  return (
    <svg viewBox="0 0 60 40" width={size} height={size * 0.67} aria-hidden>
      <rect width="60" height="13.33" y="0" fill="#00732F" />
      <rect width="60" height="13.33" y="13.33" fill="#FFFFFF" />
      <rect width="60" height="13.33" y="26.66" fill="#000000" />
      <rect width="15" height="40" fill="#EF3340" />
    </svg>
  );
}

function Piece({ piece, region }) {
  const { x, y, rot, dur, delay, size, color, isFlag } = piece;
  return (
    <motion.div
      initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0.3 }}
      animate={{
        x,
        y,
        rotate: rot,
        opacity: [0, 1, 1, 0.9, 0],
        scale: [0.3, 1, 1, 1, 0.8],
      }}
      transition={{
        duration: dur,
        delay,
        ease: [0.18, 0.9, 0.42, 1.0],
        times: [0, 0.08, 0.55, 0.85, 1],
      }}
      style={{ width: size, height: isFlag ? size * 0.67 : size * 0.36 }}
      className="absolute left-1/2 top-1/2 pointer-events-none will-change-transform"
    >
      {isFlag ? (
        region === "uk" ? (
          <UKFlag size={size} />
        ) : (
          <UAEFlag size={size} />
        )
      ) : (
        <div
          className="w-full h-full"
          style={{ background: color, borderRadius: 2 }}
        />
      )}
    </motion.div>
  );
}

export default function ConfettiBurst({ region = "uk", count = 80, onDone }) {
  const pieces = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 240 + Math.random() * 520;
      const gravity = 200 + Math.random() * 420;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance + gravity; // add gravity drop
      const rot = (Math.random() - 0.5) * 720;
      const dur = 1.5 + Math.random() * 0.9;
      const delay = Math.random() * 0.12;
      const isFlag = Math.random() < 0.22;
      const size = isFlag ? 22 + Math.random() * 14 : 8 + Math.random() * 10;
      const color =
        CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      arr.push({ x, y, rot, dur, delay, size, color, isFlag });
    }
    return arr;
  }, [count]);

  React.useEffect(() => {
    if (!onDone) return;
    const maxDur = Math.max(...pieces.map((p) => p.dur + p.delay));
    const t = setTimeout(onDone, (maxDur - 0.1) * 1000);
    return () => clearTimeout(t);
  }, [pieces, onDone]);

  return (
    <div className="fixed inset-0 z-[110] pointer-events-none" data-testid="confetti-burst">
      <div className="absolute inset-0">
        {pieces.map((p, i) => (
          <Piece key={i} piece={p} region={region} />
        ))}
      </div>
    </div>
  );
}
